import { AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { BulkMessage, SendBulkMessagePayload } from '@/types/bulkMessage';
import ajax from '@/utils/ajax';

export const bulkMessageRepository = {
  async send({
    host,
    payload
  }: {
    host: string;
    payload: SendBulkMessagePayload;
  }): Promise<BulkMessage> {
    const path = '/api/dugout/bulk-messages/';
    const response = await ajax.post<SendBulkMessagePayload, AxiosResponse<BulkMessage>>({
      url: `${host}${path}`,
      data: payload
    });
    return response.data;
  },

  async getStatus({ host, id }: { host: string; id: string }): Promise<BulkMessage> {
    const path = `/api/dugout/bulk-messages/${id}/`;
    const { data } = await ajax.get({ url: `${host}${path}` });
    return data;
  },

  async prepareAttachment({
    host,
    name,
    contentType,
    size,
    bulkMessageId
  }: {
    host: string;
    name: string;
    contentType: string;
    size: number;
    bulkMessageId: string | null;
  }): Promise<{
    bulk_message_id: string;
    attachment_id: string;
    upload_url: string;
    name: string;
    size: number;
  }> {
    const response = await ajax.post<object, AxiosResponse>({
      url: `${host}/api/dugout/bulk-messages/prepare_attachment/`,
      data: {
        name,
        content_type: contentType,
        size,
        ...(bulkMessageId ? { bulk_message_id: bulkMessageId } : {})
      }
    });
    return response.data;
  },

  async removeAttachment({
    host,
    attachmentId,
    bulkMessageId
  }: {
    host: string;
    attachmentId: string;
    bulkMessageId: string;
  }): Promise<void> {
    await ajax.delete<void, AxiosResponse>({
      url: `${host}/api/dugout/bulk-messages/attachments/${attachmentId}/`,
      config: { params: { bulk_message_id: bulkMessageId } }
    });
  }
};

const BulkMessageRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async send(payload: SendBulkMessagePayload): Promise<BulkMessage> {
      return bulkMessageRepository.send({ host, payload });
    },

    async getStatus(id: string): Promise<BulkMessage> {
      return bulkMessageRepository.getStatus({ host, id });
    },

    async prepareAttachment({
      file,
      bulkMessageId
    }: {
      file: File;
      bulkMessageId: string | null;
    }): Promise<{
      bulk_message_id: string;
      attachment_id: string;
      upload_url: string;
      name: string;
      size: number;
    }> {
      return bulkMessageRepository.prepareAttachment({
        host,
        name: file.name,
        contentType: file.type,
        size: file.size,
        bulkMessageId
      });
    },

    async removeAttachment(attachmentId: string, bulkMessageId: string): Promise<void> {
      return bulkMessageRepository.removeAttachment({ host, attachmentId, bulkMessageId });
    }
  };
};

export default BulkMessageRepository;
